import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, createElementBlock as _createElementBlock, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-d2e87da8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "edit-subnet-drawer" }
const _hoisted_2 = { class: "selected-terminals" }
const _hoisted_3 = { class: "terminal-header" }
const _hoisted_4 = { class: "drawer-footer" }

import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'

import { ScanMode, SubnetStatusItem } from '../subnet-status/subnet-status.service'
import { EditSubnetDrawerService, EditSubnetReq } from './edit-subnet-drawer.service'
import { ElMessage } from 'element-plus'
import SelectTerminalDrawer from '@/views/dashboard/_components/select-terminal-drawer/select-terminal-drawer.vue'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import CmForm from '@/components/cmForm/index.vue'
import { FormSize, FormActionType, FormType } from '@/components/cmForm/types'
import type { FormSizeType } from '@/components/cmForm/types'
type EditSubnetForm = EditSubnetReq & { terminal_selector?: undefined }


export default /*#__PURE__*/_defineComponent({
  __name: 'edit-subnet-drawer',
  props: {
  modelValue: {
    type: Boolean,
    default: false
  },
  subnetData: {
    type: Object as () => SubnetStatusItem | null,
    default: null
  }
},
  emits: ['update:modelValue', 'save'],
  setup(__props, { emit: __emit }) {

const { t, locale } = useI18n()

const props = __props

const emit = __emit

const injector = useReflectiveInjector([EditSubnetDrawerService])
const service = injector.get(EditSubnetDrawerService)

const formRef = ref()
const formSize: FormSizeType = FormSize.Small
const formType = ref(FormActionType.Other)

const loading = ref(false)
const pageLoading = ref(false)
const selectedTerminalsLoading = ref(false)
const selectTerminalDrawerVisible = ref(false)
const selectedTerminals = ref<ClientItem[]>([])

const drawerVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const form = reactive<EditSubnetForm>({
  id: undefined,
  subnet_address: '',
  scan_model: ScanMode.auto,
  remarks: ''
})

const formColumns = computed(() => {
  void locale.value
  return {
    subnet_address: {
      label: t('editSubnet.subnetAddress'),
      required: true,
      placeholder: t('editSubnet.subnetAddressPlaceholder'),
      colOpt: { span: 24 },
      disabled: () => true,
    },
    scan_model: {
      label: t('editSubnet.scanAgentMode'),
      required: true,
      type: FormType.Radio,
      dictData: [
        { label: t('scanModeLabel.specified'), value: ScanMode.specified },
        { label: t('scanModeLabel.auto'), value: ScanMode.auto },
      ],
      colOpt: { span: 24 },
    },
    terminal_selector: {
      label: t('editSubnet.selectTerminalLabel'),
      slot: true,
      colOpt: { span: 24 },
      hidden: () => form.scan_model !== ScanMode.specified,
    },
    remarks: {
      label: t('subnetStatus.remarks'),
      placeholder: t('discoveredList.pleaseEnterRemark'),
      colOpt: { span: 24 },
      inputType: 'textarea',
      inputOpt: { maxlength: 500, showWordLimit: true, rows: 4 },
    },
  }
})

watch(() => props.modelValue, (val) => {
  if (!val) return

  if (!props.subnetData?.id) {
    resetForm()
    return
  }

  loadSubnetData()
})

function resetForm () {
  form.id = undefined
  form.subnet_address = ''
  form.scan_model = ScanMode.auto
  form.remarks = ''
  selectedTerminals.value = []
}

function loadSubnetData () {
  if (!props.subnetData?.id) return

  form.id = props.subnetData.id
  form.subnet_address = props.subnetData.subnet_address || ''
  pageLoading.value = loading.value = true

  service.getSubnetSettings({ id: props.subnetData.id }).then(response => {
    Object.assign(form, response)
    getSelectedTerminals(response.device_ids)
  }).finally(() => {
    pageLoading.value = loading.value = false
  })
}

function getSelectedTerminals (deviceIds: string[]) {
  if (!deviceIds.length) return

  selectedTerminalsLoading.value = true
  service.getTerminalListByDeviceIds({
    device_ids: deviceIds,
    group_id:-1
  }).then(res => {
    selectedTerminals.value = res.terminal_list || []
  }).finally(() => {
    selectedTerminalsLoading.value = false
  })
}

function handleOpenSelectTerminal () {
  if (!form.subnet_address) {
    ElMessage.warning(t('editSubnet.pleaseEnterSubnet'))
    return
  }
  selectTerminalDrawerVisible.value = true
}

function handleTerminalSelect (terminals: Array<ClientItem>) {
  selectedTerminals.value = terminals
}

function handleRemoveTerminal (deviceId: string) {
  selectedTerminals.value = selectedTerminals.value.filter(t => t.device_id !== deviceId)
}

function handleClearAll () {
  selectedTerminals.value = []
}

function validateForm () {
  if (!form.subnet_address) {
    ElMessage.warning(t('editSubnet.pleaseEnterSubnetAddress'))
    return false
  }

  if (form.scan_model === ScanMode.specified) {
    if (selectedTerminals.value.length === 0) {
      ElMessage.warning(t('editSubnet.selectAtLeastOneTerminal'))
      return false
    }
    if (selectedTerminals.value.length > 5) {
      ElMessage.warning(t('editSubnet.maxFiveTerminals'))
      return false
    }
  }

  return true
}

function buildSaveParams (): EditSubnetReq {
  const params: EditSubnetReq = { ...form }
  if (form.scan_model === ScanMode.specified) {
    params.device_ids = selectedTerminals.value.map(t => t.device_id)
  }

  return params
}

async function handleSubmitClick () {
  const valid = await formRef.value?.handleSubmit?.()
  if (!valid) return
  if (!validateForm()) return
  loading.value = true
  try {
    const params = buildSaveParams()
    await service.editSubnet(params)
    ElMessage.success(t('editSubnet.saveSuccess'))
    emit('save')
    emit('update:modelValue', false)
  } finally {
    loading.value = false
  }
}

function handleCancel () {
  emit('update:modelValue', false)
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_drawer, {
    modelValue: drawerVisible.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((drawerVisible).value = $event)),
    title: _ctx.$t('editSubnet.titleEdit', { address: __props.subnetData?.subnet_address || '' }),
    size: 1000,
    "append-to-body": "",
    "destroy-on-close": ""
  }, {
    default: _withCtx(() => [
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(CmForm, {
          ref_key: "formRef",
          ref: formRef,
          modelValue: form,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form) = $event)),
          formSize: _unref(formSize),
          formColumns: formColumns.value,
          formOpt: { 'label-width': 'auto', labelPosition: 'left' },
          opeRowOpt: { justify: 'start', style: { marginTop: '20px' } },
          formType: formType.value,
          "onUpdate:formType": _cache[1] || (_cache[1] = ($event: any) => ((formType).value = $event)),
          style: {"flex":"1","overflow-y":"auto","overflow-x":"hidden"}
        }, {
          terminal_selector: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_el_button, {
                  type: "primary",
                  size: "small",
                  onClick: handleOpenSelectTerminal,
                  disabled: selectedTerminalsLoading.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('editSubnet.selectTerminal')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]),
                (selectedTerminals.value.length > 0)
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      type: "text",
                      size: "small",
                      onClick: handleClearAll
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('editSubnet.clear')), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_component_el_table, {
                "empty-text": _ctx.$t('common.noData'),
                data: selectedTerminals.value,
                "max-height": "300",
                style: {"margin-top":"10px","line-height":"normal","width":"100%"}
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_table_column, {
                    prop: "computer_name",
                    label: _ctx.$t('genuineSoftwareUsageColumns.computerName'),
                    "min-width": "140"
                  }, {
                    default: _withCtx(({ row }) => [
                      _createVNode(TerminalIconName, {
                        computer_name: row.computer_name,
                        system_type: row.system_type,
                        login_status: row.login_status
                      }, null, 8, ["computer_name", "system_type", "login_status"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_component_el_table_column, {
                    prop: "user_mark",
                    label: _ctx.$t('genuineSoftwareUsageColumns.registerUser'),
                    "min-width": "120",
                    "show-overflow-tooltip": ""
                  }, null, 8, ["label"]),
                  _createVNode(_component_el_table_column, {
                    prop: "group_name",
                    label: _ctx.$t('editSubnet.groupName'),
                    "min-width": "100"
                  }, null, 8, ["label"]),
                  _createVNode(_component_el_table_column, {
                    prop: "ip_address",
                    label: _ctx.$t('genuineSoftwareUsageColumns.ipAddress'),
                    "min-width": "120"
                  }, {
                    default: _withCtx((scope) => [
                      _createVNode(IPColumn, {
                        cip: scope.row.client_ip,
                        sip: scope.row.system_ip
                      }, null, 8, ["cip", "sip"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_component_el_table_column, {
                    label: _ctx.$t('commonPage.operation'),
                    width: "80",
                    fixed: "right"
                  }, {
                    default: _withCtx(({ row }) => [
                      _createVNode(_component_el_button, {
                        type: "text",
                        class: "cm-link-danger",
                        size: "small",
                        onClick: ($event: any) => (handleRemoveTerminal(row.device_id))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('editSubnet.remove')), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick"])
                    ]),
                    _: 1
                  }, 8, ["label"])
                ]),
                _: 1
              }, 8, ["empty-text", "data"])
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "formSize", "formColumns", "formType"]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_el_button, {
            size: "small",
            onClick: handleCancel
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            size: "small",
            loading: loading.value,
            onClick: handleSubmitClick
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.save')), 1)
            ]),
            _: 1
          }, 8, ["loading"])
        ]),
        _createVNode(SelectTerminalDrawer, {
          modelValue: selectTerminalDrawerVisible.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectTerminalDrawerVisible).value = $event)),
          title: _ctx.$t('editSubnet.terminalsOfSubnet', { address: __props.subnetData?.subnet_address || '' }),
          subnet_address: __props.subnetData?.subnet_address,
          "prop-selected-data": selectedTerminals.value,
          "system-types": [1,3],
          "max-count": 5,
          onConfirm: handleTerminalSelect
        }, null, 8, ["modelValue", "title", "subnet_address", "prop-selected-data"])
      ])), [
        [_directive_loading, pageLoading.value]
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})